import {
  IconChat,
  IconCompanies,
  IconNewsletter,
  IconPerson,
  IconProcesses,
  IconSettings,
} from '@assets';
import type { MenuItem } from '@utils';
import { env } from '@utils/env';

/**
 * Pulse is a separate portal, so the sidebar can only link out to it, and only where it is
 * deployed: `VITE_PULSE_URL` unset and the item is not rendered at all, rather than pointing at a
 * host that does not resolve.
 *
 * An absolute http(s) URL with a host, or nothing. A host on its own
 * ('pulse.stage.internal.easybiz.cc') is a *relative* href to a browser, so the item would open
 * `/pulse.stage.internal.easybiz.cc` on this app and land on Not found — the very thing rendering
 * an anchor rather than a NavLink avoids. A misconfigured value therefore hides the item, which is
 * the same state as not deploying Pulse.
 *
 * Hiding it used to be a soft failure, with the legacy Chats & Tasks item still in the sidebar
 * behind it. It is not any more: this is now the only chat entry there is, so a value that is
 * configured but unusable — a dropped scheme, a half-finished edit — says so on the console
 * instead of leaving a kyc user with a sidebar of one item and nothing to read it by. Unset stays
 * silent, because unset is a deployment choice rather than a mistake.
 *
 * The configured string is what ends up in the href, not `URL.href`, which would normalise a
 * trailing slash onto whatever the ConfigMap says.
 */
const pulseUrl = (() => {
  const configured = env.VITE_PULSE_URL?.trim();

  if (!configured) {
    return null;
  }

  const unusable = () => {
    console.warn(
      `VITE_PULSE_URL is not an absolute http(s) URL (${configured}); the sidebar's Pulse item is hidden.`
    );

    return null;
  };

  try {
    // No host check: http and https are "special" schemes, and the URL parser already refuses
    // those without a host ('https://' throws rather than parsing to an empty hostname).
    const { protocol } = new URL(configured);

    return protocol === 'https:' || protocol === 'http:'
      ? configured
      : unusable();
  } catch {
    // Not a URL at all — 'pulse.stage.internal.easybiz.cc', or a value someone left half-edited.
    return unusable();
  }
})();

/**
 * Whoever this app used to show Chats & Tasks to. It was derived from the `'chat'` token in
 * `accessMapping` while the legacy page still existed; that page and its token are gone, so the
 * audience is now stated here — the four roles the token was on, unchanged.
 *
 * Explicit `roles` is not optional for an external item — the default matcher looks for an allowed
 * path fragment inside `to`, and `to` is a host name here.
 *
 * Wider than the legacy chat *route* ever was: support carried `'chat'` in `accessMapping` but was
 * never on `/chat-tasks`, so the item it used to be shown only ever opened onto Access denied
 * [ENG-1197]. Pulse is deliberately still offered to support — it is a separate app with its own
 * authorization, so dropping support here would be taking the chat away from a role rather than
 * closing a door to nowhere.
 */
const PULSE_ROLES = ['admin', 'kyc', 'accountant', 'support'];

const pulseItem: MenuItem[] = pulseUrl
  ? [
      {
        name: 'Pulse',
        to: pulseUrl,
        icon: <IconChat />,
        external: true,
        roles: PULSE_ROLES,
      },
    ]
  : [];

/**
 * The sidebar's entries, in their own module so the access tests can assert against the real menu
 * rather than a copy of it.
 *
 * That is not tidiness: the Migrations entry's `roles` is the single line holding an admin-only
 * page out of the kyc and support sidebars (both are allowed the token `'incorporation'`, which
 * `filteredMenu` matches as a substring of `/incorporations`), and a test restating that literal
 * would stay green if it were deleted here. Kept out of `Sidebar.tsx` because exporting a non-component
 * from a component module breaks fast refresh.
 */
export const menuList: MenuItem[] = [
  {
    name: 'Companies',
    to: '/companies',
    icon: <IconCompanies />,
  },
  {
    name: 'Monthly Invoices',
    to: '/monthly-invoices',
    icon: <IconCompanies />,
  },
  {
    name: 'Balances',
    to: '/balance-discrepancies',
    icon: <IconCompanies />,
  },
  ...pulseItem,
  {
    name: 'Access map',
    to: '/access',
    icon: <IconPerson />,
  },
  {
    name: 'Newsletter',
    to: '/newsletter',
    icon: <IconNewsletter />,
  },
  {
    // The page and its route are still 'incorporations' — this is the label the CSMs asked for, and
    // renaming the path would break their bookmarks and the three role lists that agree on it.
    name: 'Migrations',
    to: '/incorporations',
    icon: <IconProcesses />,
    // Explicit, not inherited from accessMapping: that is matched by substring, and kyc and support
    // are allowed 'incorporation' for the per-company tab — which would put this admin-only queue in
    // their sidebar, leading to Access denied [ENG-1197].
    roles: ['admin'],
  },
  {
    name: 'Accounting usage',
    to: '/accounting-usage',
    icon: <IconCompanies />,
    roles: ['admin'],
  },
];

export const menuBottom: MenuItem[] = [
  {
    to: '/settings',
    name: 'Backoffice Team',
    icon: <IconSettings />,
  },
];
